import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  ActivityIndicator,
  TextInput,
  Linking,
  Alert,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import {
  ChevronLeft,
  Phone,
  MessageCircle,
  Clock,
  Calendar,
  User,
  BookOpen,
  MessageSquare,
  Edit3,
  Save,
  CheckCircle,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { Lead, LeadHistory, Course } from '@/types/mentor';

export default function LeadDetailsScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams();
  const [lead, setLead] = useState<Lead | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [history, setHistory] = useState<LeadHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [mentorNotes, setMentorNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    loadLeadDetails();
  }, [id]);

  const loadLeadDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        router.replace('/mentor/login');
        return;
      }

      const { data: mentorData } = await supabase
        .from('mentors')
        .select('*')
        .eq('email', user.email)
        .single();

      if (!mentorData) {
        router.replace('/mentor/login');
        return;
      }

      const { data: leadData } = await supabase
        .from('leads')
        .select('*')
        .eq('id', id)
        .or(`mentor_id.eq.${mentorData.id},mentor_email.eq.${mentorData.email}`)
        .single();

      if (leadData) {
        setLead(leadData);
        setMentorNotes(leadData.notes_from_mentor || '');

        if (leadData.course_id) {
          const { data: courseData } = await supabase
            .from('courses')
            .select('*')
            .eq('id', leadData.course_id)
            .single();

          if (courseData) {
            setCourse(courseData);
          }
        }

        const { data: historyData } = await supabase
          .from('lead_history')
          .select('*')
          .eq('lead_id', id)
          .order('created_at', { ascending: false });

        if (historyData) {
          setHistory(historyData);
        }
      }
    } catch (error) {
      console.error('Error loading lead details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    if (!lead) return;
    const formattedPhone = lead.user_phone.replace(/[^0-9]/g, '');
    Linking.openURL(`tel:${formattedPhone}`);
  };

  const handleWhatsApp = () => {
    if (!lead) return;
    const formattedPhone = lead.user_phone.replace(/[^0-9]/g, '');
    const israelPhone = formattedPhone.startsWith('0')
      ? `972${formattedPhone.substring(1)}`
      : formattedPhone;
    Linking.openURL(`whatsapp://send?phone=${israelPhone}`);
  };

  const updateLeadStatus = async (newStatus: 'pending' | 'contacted' | 'completed') => {
    if (!lead) return;

    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', lead.id);

      if (!error) {
        setLead({ ...lead, status: newStatus });
        loadLeadDetails();
        Alert.alert('הצלחה', 'הסטטוס עודכן בהצלחה');
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
      Alert.alert('שגיאה', 'לא הצלחנו לעדכן את הסטטוס');
    }
  };

  const saveNotes = async () => {
    if (!lead) return;

    setSavingNotes(true);
    try {
      const { error } = await supabase
        .from('leads')
        .update({ notes_from_mentor: mentorNotes })
        .eq('id', lead.id);

      if (!error) {
        setLead({ ...lead, notes_from_mentor: mentorNotes });
        setEditingNotes(false);
        Alert.alert('הצלחה', 'ההערות נשמרו בהצלחה');
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      Alert.alert('שגיאה', 'לא הצלחנו לשמור את ההערות');
    } finally {
      setSavingNotes(false);
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'contacted':
        return 'בטיפול';
      case 'completed':
        return 'הושלם';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'contacted':
        return '#2196F3';
      case 'completed':
        return '#4CAF50';
      default:
        return theme.colors.textLight;
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>טוען פרטי ליד...</Text>
      </View>
    );
  }

  if (!lead) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorText}>ליד לא נמצא</Text>
        <Pressable onPress={() => router.back()} style={styles.backButtonLarge}>
          <Text style={styles.backButtonText}>חזור</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>פרטי ליד</Text>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ChevronLeft color={theme.colors.text} size={24} />
        </Pressable>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <User color={theme.colors.primary} size={24} />
            <Text style={styles.sectionTitle}>פרטי הלקוח</Text>
          </View>

          <View style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>שם:</Text>
              <Text style={styles.infoValue}>{lead.user_name}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>טלפון:</Text>
              <Text style={styles.infoValue}>{lead.user_phone}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>זמן מועדף:</Text>
              <Text style={styles.infoValue}>
                {lead.availability === 'morning' && 'בוקר'}
                {lead.availability === 'afternoon' && 'צהריים'}
                {lead.availability === 'evening' && 'ערב'}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>פלטפורמה:</Text>
              <Text style={styles.infoValue}>
                {lead.platform === 'phone' && 'טלפון'}
                {lead.platform === 'whatsapp' && 'WhatsApp'}
                {lead.platform === 'zoom' && 'Zoom'}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>תאריך יצירה:</Text>
              <Text style={styles.infoValue}>
                {new Date(lead.created_at).toLocaleDateString('he-IL', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <BookOpen color={theme.colors.primary} size={24} />
            <Text style={styles.sectionTitle}>פרטי הקורס</Text>
          </View>

          <View style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>שם הקורס:</Text>
              <Text style={styles.infoValue}>{lead.course_name}</Text>
            </View>
            {course && (
              <>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>תיאור:</Text>
                  <Text style={styles.infoValue}>{course.description}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>מחיר:</Text>
                  <Text style={styles.infoValue}>{course.price || 'לא צוין'}</Text>
                </View>
              </>
            )}
          </View>
        </View>

        {lead.notes && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <MessageSquare color={theme.colors.primary} size={24} />
              <Text style={styles.sectionTitle}>הערות מהלקוח</Text>
            </View>

            <View style={styles.notesCard}>
              <Text style={styles.notesText}>{lead.notes}</Text>
            </View>
          </View>
        )}

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Edit3 color={theme.colors.primary} size={24} />
            <Text style={styles.sectionTitle}>הערות מנטור</Text>
          </View>

          {editingNotes ? (
            <View style={styles.notesEditCard}>
              <TextInput
                style={styles.notesInput}
                value={mentorNotes}
                onChangeText={setMentorNotes}
                placeholder="הוסף הערות פנימיות..."
                placeholderTextColor={theme.colors.textLight}
                multiline
                textAlign="right"
              />
              <View style={styles.notesActions}>
                <Pressable
                  style={styles.saveButton}
                  onPress={saveNotes}
                  disabled={savingNotes}
                >
                  {savingNotes ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <>
                      <Save color="#FFFFFF" size={20} />
                      <Text style={styles.saveButtonText}>שמור</Text>
                    </>
                  )}
                </Pressable>
                <Pressable
                  style={styles.cancelButton}
                  onPress={() => {
                    setEditingNotes(false);
                    setMentorNotes(lead.notes_from_mentor || '');
                  }}
                  disabled={savingNotes}
                >
                  <Text style={styles.cancelButtonText}>ביטול</Text>
                </Pressable>
              </View>
            </View>
          ) : (
            <Pressable
              style={styles.notesCard}
              onPress={() => setEditingNotes(true)}
            >
              {mentorNotes ? (
                <Text style={styles.notesText}>{mentorNotes}</Text>
              ) : (
                <Text style={styles.notesPlaceholder}>
                  לחץ להוספת הערות פנימיות...
                </Text>
              )}
            </Pressable>
          )}
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Clock color={theme.colors.primary} size={24} />
            <Text style={styles.sectionTitle}>היסטוריית שינויים</Text>
          </View>

          {history.length === 0 ? (
            <Text style={styles.emptyText}>אין שינויים להצגה</Text>
          ) : (
            <View style={styles.historyList}>
              {history.map((item) => (
                <View key={item.id} style={styles.historyItem}>
                  <Text style={styles.historyText}>
                    סטטוס שונה מ-
                    <Text style={styles.historyStatus}>
                      {getStatusText(item.old_status || 'pending')}
                    </Text>
                    {' ל-'}
                    <Text style={[styles.historyStatus, { color: getStatusColor(item.new_status) }]}>
                      {getStatusText(item.new_status)}
                    </Text>
                  </Text>
                  <Text style={styles.historyDate}>
                    {new Date(item.created_at).toLocaleString('he-IL')}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        <View style={styles.statusSection}>
          <Text style={styles.statusSectionTitle}>עדכן סטטוס</Text>
          <View style={styles.statusButtons}>
            <Pressable
              style={[
                styles.statusButton,
                lead.status === 'pending' && styles.statusButtonActive,
              ]}
              onPress={() => updateLeadStatus('pending')}
            >
              <Text
                style={[
                  styles.statusButtonText,
                  lead.status === 'pending' && styles.statusButtonTextActive,
                ]}
              >
                ממתין
              </Text>
            </Pressable>

            <Pressable
              style={[
                styles.statusButton,
                lead.status === 'contacted' && styles.statusButtonActive,
              ]}
              onPress={() => updateLeadStatus('contacted')}
            >
              <Text
                style={[
                  styles.statusButtonText,
                  lead.status === 'contacted' && styles.statusButtonTextActive,
                ]}
              >
                בטיפול
              </Text>
            </Pressable>

            <Pressable
              style={[
                styles.statusButton,
                lead.status === 'completed' && styles.statusButtonActive,
              ]}
              onPress={() => updateLeadStatus('completed')}
            >
              <Text
                style={[
                  styles.statusButtonText,
                  lead.status === 'completed' && styles.statusButtonTextActive,
                ]}
              >
                הושלם
              </Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>

      <View style={styles.bottomActions}>
        <Pressable style={styles.callButton} onPress={handleCall}>
          <Phone color="#FFFFFF" size={24} />
          <Text style={styles.actionButtonText}>התקשר</Text>
        </Pressable>

        <Pressable style={styles.whatsappButton} onPress={handleWhatsApp}>
          <MessageCircle color="#FFFFFF" size={24} />
          <Text style={styles.actionButtonText}>WhatsApp</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  errorText: {
    fontSize: 18,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  backButtonLarge: {
    marginTop: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.lg,
  },
  backButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.md,
    backgroundColor: theme.colors.backgroundLight,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    paddingBottom: 100,
  },
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  infoCard: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  infoLabel: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  infoValue: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    flex: 1,
    textAlign: 'right',
  },
  notesCard: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  notesText: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 22,
  },
  notesPlaceholder: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    fontStyle: 'italic',
  },
  notesEditCard: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    gap: theme.spacing.md,
  },
  notesInput: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  notesActions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  saveButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  saveButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#FFFFFF',
  },
  cancelButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  cancelButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  historyList: {
    gap: theme.spacing.sm,
  },
  historyItem: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  historyText: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    marginBottom: theme.spacing.xs,
  },
  historyStatus: {
    fontFamily: theme.fonts.bold,
  },
  historyDate: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    paddingVertical: theme.spacing.lg,
  },
  statusSection: {
    marginTop: theme.spacing.lg,
  },
  statusSectionTitle: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  },
  statusButtons: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  statusButton: {
    flex: 1,
    paddingVertical: theme.spacing.md,
    alignItems: 'center',
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  statusButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  statusButtonText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  statusButtonTextActive: {
    color: '#FFFFFF',
    fontFamily: theme.fonts.bold,
  },
  bottomActions: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    gap: theme.spacing.md,
    padding: theme.spacing.lg,
    paddingBottom: Platform.OS === 'ios' ? 30 : theme.spacing.lg,
    backgroundColor: theme.colors.backgroundLight,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  callButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
  },
  whatsappButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: '#25D366',
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
  },
  actionButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#FFFFFF',
  },
});
